import { createAsyncThunk } from '@reduxjs/toolkit';

import type { EventoSimulacao, Config } from '../../types';
import { maiorSeq } from '../../utils/eventosDaSimulacao';
import { progressoDaExportacaoRecebido } from '../eventosDoStore';
import { paraErroDaSimulacao, registrarFalha } from '../falhasDaSimulacao';
import { selectEstadoDaSimulacao as estadoDaFeature } from '../selectors';
import { servicosDaSimulacao } from '../servicosDaSimulacao';

/**
 * Uma operação lê uma página; o cursor e o seq conhecido permitem continuar na tela ou no polling.
 */
export const buscarEventosDaSimulacao = createAsyncThunk<
  { simulacaoId: string; eventos: EventoSimulacao[]; ultimoSeq: number },
  { simulacaoId: string; aposSeq: number },
  Config
>(
  'negociacaoSimulacao/buscarEventos',
  async ({ simulacaoId, aposSeq }, { getState, rejectWithValue }) => {
    const { falhaDaSincronizacao } = estadoDaFeature(getState());
    try {
      const pagina = await servicosDaSimulacao(getState()).buscarEventos(simulacaoId, aposSeq);
      if (maiorSeq(pagina.eventos, aposSeq) <= aposSeq && pagina.ultimoSeq > aposSeq)
        throw new Error('A página não avançou o cursor do histórico');
      return { simulacaoId, ...pagina };
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      if (!falhaDaSincronizacao.eventos)
        registrarFalha('buscarEventos', erro, falha, { simulacaoId });
      return rejectWithValue(falha);
    }
  },
  { condition: (_, { getState }) => !estadoDaFeature(getState()).trocandoEscritorio },
);

/** Exportar é a operação explícita que percorre o restante; a conversa lê uma página por vez. */
export const exportarEventosDaSimulacao = createAsyncThunk<EventoSimulacao[], string, Config>(
  'negociacaoSimulacao/exportarEventos',
  async (simulacaoId, { dispatch, getState, rejectWithValue }) => {
    const escritorioId = estadoDaFeature(getState()).escritorioId;
    try {
      let alvo: number | null = null;
      do {
        const atual = estadoDaFeature(getState());
        if (atual.simulacao?.id !== simulacaoId || atual.escritorioId !== escritorioId)
          throw new Error('O contexto da exportação mudou');
        const pagina = await dispatch(
          buscarEventosDaSimulacao({ simulacaoId, aposSeq: atual.ultimoSeq }),
        );
        if (!buscarEventosDaSimulacao.fulfilled.match(pagina))
          return rejectWithValue(pagina.payload ?? paraErroDaSimulacao(pagina.error));
        const recebido = estadoDaFeature(getState());
        if (recebido.simulacao?.id !== simulacaoId || recebido.escritorioId !== escritorioId)
          throw new Error('O contexto da exportação mudou');
        alvo ??= pagina.payload.ultimoSeq;
        const seqFinal = alvo;
        dispatch(
          progressoDaExportacaoRecebido({
            simulacaoId,
            carregados: recebido.eventos.filter((evento) => evento.seq <= seqFinal).length,
            total: alvo,
          }),
        );
      } while (estadoDaFeature(getState()).ultimoSeq < alvo);
      const seqFinal = alvo;
      return estadoDaFeature(getState()).eventos.filter((evento) => evento.seq <= seqFinal);
    } catch (erro) {
      return rejectWithValue(paraErroDaSimulacao(erro));
    }
  },
  { condition: (_, { getState }) => !estadoDaFeature(getState()).exportacao.carregando },
);
